import React, { useState } from "react";
import { Download } from "lucide-react";
import { Order } from "../../../interfaces/OrderObject";
import { downloadBillImage } from "../../../services/helpers/BillImageHelper";
import { useNotification } from "../contexts/NotificationContext";
import { NotificationType } from "../../../constants/Enums";

interface DownloadBillButtonProps {
  /** A billed order — only its `bill` snapshot and totals are used. */
  order: Order;
  businessName: string;
  businessAddress?: string | null;
  className?: string;
  label?: string;
}

/**
 * "Download bill" for a dine-in order: renders the standard receipt image
 * (BillImageHelper) and saves or shares it. Shows its own loading state, so
 * callers only place it.
 */
const DownloadBillButton: React.FC<DownloadBillButtonProps> = ({
  order,
  businessName,
  businessAddress,
  className = "bill__btn",
  label = "Download bill",
}) => {
  const { triggerNotification } = useNotification();
  const [saving, setSaving] = useState(false);

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      await downloadBillImage(order, { businessName, businessAddress });
    } catch (error) {
      // A dismissed share sheet rejects with AbortError — not a failure.
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        triggerNotification(
          error instanceof Error ? error.message : "Couldn't download the bill.",
          NotificationType.ERROR
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <button type="button" className={className} onClick={handleClick} disabled={saving || !order.bill}>
      <Download size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
      {saving ? "Preparing…" : label}
    </button>
  );
};

export default DownloadBillButton;
